// ==========================
// Version 2: pages/About.js
// - Rebuilds the page around the complete BWK founder story
// - Connects the desert origin to proof, ownership and permanence
// ==========================
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import BrandLogo from "../components/BrandLogo";
import Seo from "../components/Seo";
import { useAuth } from "../context/AuthContext";
import "./About.css";

export default function About() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const pageUrl =
    typeof window !== "undefined" ? `${window.location.origin}/about` : undefined;

  const beginMemory = () => {
    navigate(currentUser ? "/home" : "/register");
  };

  return (
    <main className="bwak-about">
      <Seo
        title="Our Story | Blessed with a Kiss"
        description="A kiss beneath the desert stars inspired a new way to preserve, prove and own the memories that shape our lives."
        image="/images/desert-night-origin-memory.jpg"
        url={pageUrl}
        type="website"
      />

      <section className="bwak-about__hero" aria-labelledby="about-title">
        <div className="bwak-about__stars" aria-hidden="true" />
        <div className="bwak-about__horizon" aria-hidden="true">
          <span className="bwak-about__dune bwak-about__dune--back" />
          <span className="bwak-about__dune bwak-about__dune--front" />
        </div>

        <div className="bwak-about__hero-content">
          <p className="bwak-about__eyebrow">Our story began beneath the stars</p>
          <h1 id="about-title">One kiss. One place. One question.</h1>
          <blockquote>
            “Has anyone ever kissed exactly here before?”
          </blockquote>
          <p className="bwak-about__hero-intro">
            In the middle of a desert in Iran, a private moment became the beginning
            of Blessed with a Kiss.
          </p>
          <a className="bwak-about__scroll-cue" href="#the-night">
            Read the story
            <span aria-hidden="true">↓</span>
          </a>
        </div>
      </section>

      <section id="the-night" className="bwak-about__story-section">
        <div className="bwak-about__story-layout">
          <div className="bwak-about__chapter-mark" aria-hidden="true">
            <span>01</span>
            <div />
          </div>

          <article className="bwak-about__story-copy">
            <p className="bwak-about__eyebrow bwak-about__eyebrow--light">
              The night everything started
            </p>
            <h2>A place became part of our story.</h2>
            <p className="bwak-about__lead-paragraph">
              Years before BWK became a product, its founder and his then-girlfriend,
              who is now his wife, were travelling by car through one of the deserts
              of Iran.
            </p>
            <p>
              It was around two or three in the morning. The road was empty. There
              were no cities, villages, buildings or artificial lights nearby. The
              nearest building was believed to be roughly 150 kilometres away.
            </p>
            <p>
              They stopped the car, walked a few metres into the desert and lay down
              together on the sand beneath a sky full of stars. They talked for a long
              time about life, their future and their dreams. At some point, they kissed.
            </p>
            <p>
              The place itself was not a landmark. It had no name and no reason to
              appear on a map. Yet, because of what happened there, that small patch
              of desert had become meaningful. It had become theirs.
            </p>
          </article>

          <aside className="bwak-about__night-card" aria-label="The first BWK memory">
            <img
              className="bwak-about__night-card-image"
              src="/images/desert-night-origin-memory.jpg"
              alt="A fictional couple kissing beneath the stars beside a remote desert road in Iran"
            />
            <div className="bwak-about__night-card-body">
              <span className="bwak-about__memory-label">The first memory</span>
              <h3>A desert night in Iran</h3>
              <dl>
                <div>
                  <dt>Time</dt>
                  <dd>About 2 to 3 AM</dd>
                </div>
                <div>
                  <dt>Distance</dt>
                  <dd>150 km from the nearest building</dd>
                </div>
                <div>
                  <dt>Beginning</dt>
                  <dd>One question beneath the stars</dd>
                </div>
              </dl>
            </div>
          </aside>
        </div>
      </section>

      <section className="bwak-about__question-section">
        <div className="bwak-about__narrow">
          <p className="bwak-about__eyebrow bwak-about__eyebrow--light">
            The question beneath the question
          </p>
          <h2>What gives a place meaning?</h2>
          <p>
            The question was about more than whether another couple had stood there
            before. It opened a larger thought: every meaningful memory happens
            somewhere. A first kiss, a promise, a goodbye, a reunion, a quiet moment
            with someone we love. The geography may be ordinary, but what happened
            there can make it sacred to us.
          </p>
          <div className="bwak-about__pullquote">
            <span aria-hidden="true">“</span>
            <p>
              What if people could leave a lasting trace of the moments that made a
              place meaningful?
            </p>
          </div>
          <p>
            That thought gave the idea its name. A place could be blessed by a kiss,
            not because the world recognised it, but because two people would always
            remember what happened there.
          </p>
        </div>
      </section>

      <section className="bwak-about__map-section" aria-labelledby="places-title">
        <div className="bwak-about__map-visual" aria-hidden="true">
          <span className="bwak-about__route bwak-about__route--one" />
          <span className="bwak-about__route bwak-about__route--two" />
          <span className="bwak-about__map-pin bwak-about__map-pin--one" />
          <span className="bwak-about__map-pin bwak-about__map-pin--two" />
          <span className="bwak-about__map-pin bwak-about__map-pin--three" />
          <div className="bwak-about__map-count">
            <strong>150+</strong>
            <span>places remembered</span>
          </div>
        </div>

        <div className="bwak-about__map-copy">
          <p className="bwak-about__eyebrow bwak-about__eyebrow--light">
            The idea before the product
          </p>
          <h2>For years, BWK lived inside Google Maps.</h2>
          <p>
            The idea did not disappear after that night. Throughout their relationship
            and after they married, the founder continued saving the places that mattered
            to them.
          </p>
          <p>
            More than 150 locations were collected under one label: Blessed with a Kiss.
            Each pin held an invisible story. In a way, the product already existed. A
            map was simply standing in for the home those memories did not yet have.
          </p>
          <p>
            The first vision was simple: connect a place with its date, words, photos,
            videos or voice, then preserve the complete meaning of the moment.
          </p>
        </div>
      </section>

      <section className="bwak-about__evolution-section">
        <div className="bwak-about__section-heading">
          <p className="bwak-about__eyebrow">What BWK became</p>
          <h2>From a pin on a map to proof that belongs to you.</h2>
          <p>
            Remembering the place was only the beginning. The deeper purpose was to
            protect the meaning of the memory itself.
          </p>
        </div>

        <div className="bwak-about__principles">
          <article>
            <span>01</span>
            <h3>Preserve</h3>
            <p>Bring the story, place, date and media together as one lasting memory.</p>
          </article>
          <article>
            <span>02</span>
            <h3>Prove</h3>
            <p>Create a verifiable record of the memory and when it was immortalised.</p>
          </article>
          <article>
            <span>03</span>
            <h3>Own</h3>
            <p>Turn the memory into a unique digital asset held in your crypto wallet.</p>
          </article>
        </div>

        <div className="bwak-about__proof-note">
          <div className="bwak-about__proof-icon" aria-hidden="true">✓</div>
          <div>
            <h3>Blockchain came later. The human reason came first.</h3>
            <p>
              The technology is not the story. It is what allows a story to carry
              verifiable proof, distinct ownership and a permanent digital identity.
              Minting is the moment a personal memory becomes an owned digital keepsake.
            </p>
          </div>
        </div>
      </section>

      <section className="bwak-about__future-section">
        <div className="bwak-about__future-glow" aria-hidden="true" />
        <div className="bwak-about__narrow bwak-about__future-content">
          <p className="bwak-about__eyebrow">Why this matters now</p>
          <h2>In a world of unlimited content, real human stories will become scarce.</h2>
          <p>
            AI can already generate convincing images, voices, stories and identities.
            As generated content grows, authentic memories and lived experiences will
            become harder to recognise, and more valuable to prove.
          </p>
          <p>
            BWK is building a way for anyone to establish the origin and ownership of
            their story. Not to turn life into speculation, but to give real moments a
            traceable history and a place in our digital future.
          </p>
          <p className="bwak-about__future-statement">
            Your life was lived by you. Your story should be yours to prove.
          </p>
        </div>
      </section>

      <section className="bwak-about__promise-section">
        <BrandLogo size={64} decorative framed />
        <p className="bwak-about__eyebrow bwak-about__eyebrow--light">
          The promise at the heart of BWK
        </p>
        <h2>What mattered should not disappear.</h2>
        <p>
          The map, the story, the photographs and the digital proof all serve one
          purpose: to carry a meaningful human moment into the future.
        </p>
        <button
          type="button"
          className="bwak-about__primary-button"
          onClick={beginMemory}
        >
          {currentUser ? "Immortalise a memory" : "Begin your first memory"}
        </button>
        <Link className="bwak-about__map-link" to="/map">
          Explore the global map <span aria-hidden="true">→</span>
        </Link>
      </section>
    </main>
  );
}
